import React, { createContext, useContext, useState, useEffect } from 'react';
import { GroupedProduct } from '../lib/supabase';

interface ColorOption {
  name: string;
  code?: string;
  rgb?: string;
  image?: string;
}

interface CartItem {
  id: string;
  name: string;
  brand: string;
  price: number;
  quantity: number;
  image: string;
  styleCode: string;
  selectedColor?: string;
  selectedSize?: string;
  availableColors?: ColorOption[];
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (product: GroupedProduct, quantity?: number, color?: string, size?: string) => void;
  removeFromCart: (id: string) => void;
  incrementQuantity: (id: string) => void;
  decrementQuantity: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  isInCart: (id: string) => boolean;
  getCartItemCount: () => number;
  subtotal: number;
  tax: number;
  shipping: number;
  total: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>(() => {
    const savedCart = localStorage.getItem('outpost-cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('outpost-cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product: GroupedProduct, quantity = 1, color?: string, size?: string) => {
    const itemId = `${product.style_code}-${color || 'default'}-${size || 'default'}`;

    setCart(prev => {
      const existingItem = prev.find(item => item.id === itemId);

      if (existingItem) {
        return prev.map(item =>
          item.id === itemId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      // Extract available colors from product
      const availableColors: ColorOption[] = product.colors?.map(c => ({
        name: c.colour_name,
        code: c.colour_code,
        rgb: c.rgb,
        image: c.colour_image,
      })) || [];

      return [
        ...prev,
        {
          id: itemId,
          name: product.style_name,
          brand: product.brand,
          price: product.price_range.min,
          quantity,
          image: product.primary_product_image_url,
          styleCode: product.style_code,
          selectedColor: color,
          selectedSize: size,
          availableColors,
        },
      ];
    });
  };

  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const incrementQuantity = (id: string) => {
    setCart(prev =>
      prev.map(item =>
        item.id === id ? { ...item, quantity: item.quantity + 1 } : item
      )
    );
  };

  const decrementQuantity = (id: string) => {
    setCart(prev =>
      prev.map(item =>
        item.id === id && item.quantity > 1
          ? { ...item, quantity: item.quantity - 1 }
          : item
      ).filter(item => item.quantity > 0)
    );
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(id);
      return;
    }

    setCart(prev =>
      prev.map(item =>
        item.id === id ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const isInCart = (styleCode: string) => {
    return cart.some(item => item.styleCode === styleCode);
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  // Calculate totals
  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const tax = subtotal * 0.2; // 20% VAT
  const shipping = subtotal >= 50 ? 0 : 4.99; // Free shipping over £50
  const total = subtotal + tax + shipping;

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        incrementQuantity,
        decrementQuantity,
        updateQuantity,
        clearCart,
        isInCart,
        getCartItemCount,
        subtotal,
        tax,
        shipping,
        total,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Utility functions
export const cartUtils = {
  formatPrice: (price: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(price);
  },
};
